// src/screens/IncomeScreen.tsx
// ─────────────────────────────────────────────────────────────────────────────
// Brief 34 Section 6 — Your Income
// Shows income sources, employee equivalent, and UK earnings percentile.
// ─────────────────────────────────────────────────────────────────────────────

import React, { useMemo } from 'react';
import { ClientData } from '../types/ClientData';
import { getTaxYearForDate, TAX_YEARS } from '../taxConfig';
import { formatCurrency } from '../utils/format';

interface Props {
  data: ClientData;
  onUpdate: (updates: Partial<ClientData>) => void;
}

const fmt = formatCurrency;

// ── Earnings percentile lookup (HMRC/ONS gross annual salary, full-time) ────
function getEarningsPercentile(grossEquiv: number): number {
  if (grossEquiv >= 200000) return 99;
  if (grossEquiv >= 100000) return 97;
  if (grossEquiv >= 80000)  return 95;
  if (grossEquiv >= 60000)  return 90;
  if (grossEquiv >= 50000)  return 85;
  if (grossEquiv >= 45000)  return 80;
  if (grossEquiv >= 40000)  return 75;
  if (grossEquiv >= 35000)  return 70;
  if (grossEquiv >= 30000)  return 60;
  if (grossEquiv >= 25000)  return 50;
  if (grossEquiv >= 20000)  return 40;
  if (grossEquiv >= 15000)  return 25;
  return 10;
}

// ── Tax calculation (copied from IncomeGrossToNet.tsx) ──────────────────────
function calcTax(data: ClientData) {
  const taxYear = data.taxYearOverride ?? getTaxYearForDate(data.sessionDate || new Date().toISOString());
  const r = TAX_YEARS[taxYear];

  const salary     = data.salary || 0;
  const payeIncome = data.payeIncome || 0;
  const rentalNet  = data.rentalIncomeNet || 0;
  const savingsInt = data.savingsInterest || 0;
  const companyDiv = data.dividends || 0;
  const otherDiv   = data.otherDividends || 0;
  const expenses   = (data.expensesReimbursement || 0) * 12;

  const nonSavingsIncome = salary + payeIncome + rentalNet;
  const savingsIncome    = savingsInt;
  const totalDividends   = companyDiv + otherDiv;
  const totalTaxableIncome = nonSavingsIncome + savingsIncome + totalDividends;

  // Personal allowance with taper
  let effectivePA: number = r.personalAllowance;
  if (totalTaxableIncome > 100000) {
    const taper = Math.floor((totalTaxableIncome - 100000) / 2);
    effectivePA = Math.max(0, r.personalAllowance - taper);
  }

  const bandPA = effectivePA;
  const bandBasicTop = r.basicRateLimit;
  const bandHigherTop = r.higherRateLimit;

  function fillBands(amount: number, cursor: number) {
    let remaining = amount;
    let inPA = 0, inBasic = 0, inHigher = 0, inAdditional = 0;
    if (cursor < bandPA && remaining > 0) {
      const fill = Math.min(remaining, bandPA - cursor); inPA = fill; cursor += fill; remaining -= fill;
    }
    if (cursor < bandBasicTop && remaining > 0) {
      const fill = Math.min(remaining, bandBasicTop - cursor); inBasic = fill; cursor += fill; remaining -= fill;
    }
    if (cursor < bandHigherTop && remaining > 0) {
      const fill = Math.min(remaining, bandHigherTop - cursor); inHigher = fill; cursor += fill; remaining -= fill;
    }
    if (remaining > 0) { inAdditional = remaining; cursor += remaining; }
    return { inPA, inBasic, inHigher, inAdditional, newCursor: cursor };
  }

  const ns = fillBands(nonSavingsIncome, 0);
  const sv = fillBands(savingsIncome, ns.newCursor);
  const dv = fillBands(totalDividends, sv.newCursor);

  const itNonSavings = ns.inBasic * r.incomeTaxBasic + ns.inHigher * r.incomeTaxHigher + ns.inAdditional * r.incomeTaxAdditional;

  const highestNonSavingsBand = ns.inAdditional > 0 ? 'additional' : ns.inHigher > 0 ? 'higher' : 'basic';
  const psa = highestNonSavingsBand === 'additional' ? 0 : highestNonSavingsBand === 'higher' ? 500 : 1000;
  const taxableSavings = Math.max(0, savingsIncome - psa);
  const svTaxableBasic = Math.max(0, Math.min(sv.inBasic, taxableSavings));
  const svTaxableHigher = Math.max(0, Math.min(sv.inHigher, taxableSavings - svTaxableBasic));
  const svTaxableAdditional = Math.max(0, taxableSavings - svTaxableBasic - svTaxableHigher);
  const itSavings = svTaxableBasic * r.incomeTaxBasic + svTaxableHigher * r.incomeTaxHigher + svTaxableAdditional * r.incomeTaxAdditional;

  const taxableDivs = Math.max(0, totalDividends - r.dividendAllowance);
  const dvTaxableBasic = Math.min(dv.inBasic, taxableDivs);
  const dvTaxableHigher = Math.min(dv.inHigher, Math.max(0, taxableDivs - dvTaxableBasic));
  const dvTaxableAdditional = Math.max(0, taxableDivs - dvTaxableBasic - dvTaxableHigher);
  const dividendTax = dvTaxableBasic * r.dividendBasicRate + dvTaxableHigher * r.dividendHigherRate + dvTaxableAdditional * r.dividendAdditionalRate;

  const niableSalary = Math.max(0, salary - r.niPrimaryThreshold);
  const niBasicAmount = Math.min(niableSalary, Math.max(0, r.niUpperEarningsLimit - r.niPrimaryThreshold));
  const niUpperAmount = Math.max(0, niableSalary - niBasicAmount);
  const niEmployee = niBasicAmount * r.niEmployeeRate + niUpperAmount * r.niEmployeeUpperRate;

  let slUndergradAnnual = 0;
  const slPlan = data.studentLoanUndergrad || 'none';
  if (slPlan !== 'none') {
    const threshold = slPlan === 'plan1' ? r.slPlan1Threshold : slPlan === 'plan2' ? r.slPlan2Threshold : r.slPlan5Threshold;
    slUndergradAnnual = Math.max(0, totalTaxableIncome - threshold) * r.slUndergradRate;
  }
  const slPostgradAnnual = data.studentLoanPostgrad ? Math.max(0, totalTaxableIncome - r.slPostgradThreshold) * r.slPostgradRate : 0;
  const slTotalAnnual = slUndergradAnnual + slPostgradAnnual;

  const totalAnnualTax = itNonSavings + itSavings + dividendTax + niEmployee + slTotalAnnual;
  const totalMonthlyTax = totalAnnualTax / 12;
  const grossMonthly = (totalTaxableIncome / 12) + (expenses / 12);
  const netMonthly = grossMonthly - totalMonthlyTax;

  // Equivalent employee salary (binary search)
  const netAnnualExExpenses = (netMonthly * 12) - expenses;
  function calcNetFromGross(gross: number): number {
    const pa = gross > 100000 ? Math.max(0, r.personalAllowance - Math.floor((gross - 100000) / 2)) : r.personalAllowance;
    const taxable = Math.max(0, gross - pa);
    const basicBand = Math.max(0, r.basicRateLimit - pa);
    const inBasicE = Math.min(taxable, basicBand);
    const inHigherE = Math.min(Math.max(0, taxable - basicBand), r.higherRateLimit - r.basicRateLimit);
    const inAdditionalE = Math.max(0, taxable - basicBand - (r.higherRateLimit - r.basicRateLimit));
    const incomeTax = inBasicE * r.incomeTaxBasic + inHigherE * r.incomeTaxHigher + inAdditionalE * r.incomeTaxAdditional;
    const niable = Math.max(0, gross - r.niPrimaryThreshold);
    const niBasic = Math.min(niable, Math.max(0, r.niUpperEarningsLimit - r.niPrimaryThreshold));
    const niUpper = Math.max(0, niable - niBasic);
    const ni = niBasic * r.niEmployeeRate + niUpper * r.niEmployeeUpperRate;
    return gross - incomeTax - ni;
  }
  let eqLow = 0, eqHigh = netAnnualExExpenses * 3;
  for (let i = 0; i < 50; i++) {
    const mid = (eqLow + eqHigh) / 2;
    if (calcNetFromGross(mid) < netAnnualExExpenses) eqLow = mid; else eqHigh = mid;
  }
  const equivalentGrossSalary = Math.round((eqLow + eqHigh) / 2);

  return {
    totalAnnualTax,
    totalMonthlyTax,
    grossMonthly,
    netMonthly,
    equivalentGrossSalary,
    totalTaxableIncome,
    expenses,
    itNonSavings,
    itSavings,
    dividendTax,
    niEmployee,
    slTotalAnnual,
    effectivePersonalRate: totalTaxableIncome > 0 ? totalAnnualTax / totalTaxableIncome : 0,
    nonSavingsIncome,
    totalDividends,
    ns, sv, dv,
    effectivePA,
    bandPA,
    bandBasicTop,
    bandHigherTop,
    rates: r,
    taxYear,
  };
}

export default function IncomeScreen({ data }: Props) {
  const t = useMemo(() => calcTax(data), [data]);

  // Income sources
  const salary = data.salary || 0;
  const dividends = data.dividends || 0;
  const rentalIncome = data.rentalIncomeNet || 0;
  const expensesMonthly = data.expensesReimbursement || 0;
  const totalGrossAnnual = salary + dividends + rentalIncome + (expensesMonthly * 12);
  const totalGrossMonthly = totalGrossAnnual / 12;

  // Percentile
  const percentile = getEarningsPercentile(t.equivalentGrossSalary);
  const topPct = 100 - percentile;

  return (
    <div className="flex flex-col h-full">
      <div className="flex-1 overflow-y-auto p-6 max-w-5xl mx-auto w-full">
        {/* Header */}
        <div className="text-center mb-6">
          <h2 className="text-2xl font-bold text-ma-navy">Your Income</h2>
          {data.clientName && (
            <p className="text-gray-500 mt-1 text-sm">{data.clientName}</p>
          )}
        </div>

        {/* Income mix proportional bar */}
        {totalGrossAnnual > 0 && (() => {
          const salaryPct = (salary / totalGrossAnnual) * 100;
          const dividendsPct = (dividends / totalGrossAnnual) * 100;
          const rentalPct = (rentalIncome / totalGrossAnnual) * 100;
          const expensesPct = ((expensesMonthly * 12) / totalGrossAnnual) * 100;
          return (
            <div className="bg-white rounded-2xl border border-gray-200 p-6 mb-6">
              <div className="flex justify-between items-baseline mb-3">
                <h3 className="text-sm font-bold text-ma-navy uppercase tracking-wide">Your Income Mix</h3>
                <span className="text-xs text-gray-400">{fmt(totalGrossAnnual)} gross/yr</span>
              </div>
              <div className="flex h-10 rounded-full overflow-hidden">
                {salary > 0 && (
                  <div
                    className="flex items-center justify-center text-xs font-semibold text-white"
                    style={{ width: `${salaryPct}%`, background: '#1E3A5F' }}
                    title={`Salary: ${fmt(salary)}/yr`}
                  >
                    {salaryPct >= 8 ? `Salary ${salaryPct.toFixed(0)}%` : ''}
                  </div>
                )}
                {dividends > 0 && (
                  <div
                    className="flex items-center justify-center text-xs font-semibold"
                    style={{ width: `${dividendsPct}%`, background: '#6EE7B7', color: '#001F8C' }}
                    title={`Dividends: ${fmt(dividends)}/yr`}
                  >
                    {dividendsPct >= 8 ? `Dividends ${dividendsPct.toFixed(0)}%` : ''}
                  </div>
                )}
                {rentalIncome > 0 && (
                  <div
                    className="flex items-center justify-center text-xs font-semibold"
                    style={{ width: `${rentalPct}%`, background: '#C9A84C', color: '#001F8C' }}
                    title={`Rental: ${fmt(rentalIncome)}/yr`}
                  >
                    {rentalPct >= 8 ? `Rental ${rentalPct.toFixed(0)}%` : ''}
                  </div>
                )}
                {expensesMonthly > 0 && (
                  <div
                    className="flex items-center justify-center text-xs font-semibold text-gray-600"
                    style={{ width: `${expensesPct}%`, background: '#E5E7EB' }}
                    title={`Expenses: ${fmt(expensesMonthly * 12)}/yr`}
                  >
                    {expensesPct >= 8 ? `Expenses ${expensesPct.toFixed(0)}%` : ''}
                  </div>
                )}
              </div>
              <div className="flex flex-wrap gap-x-4 gap-y-1 mt-3 text-[11px] text-gray-500">
                {salary > 0 && (
                  <span className="flex items-center gap-1.5">
                    <span className="inline-block w-2.5 h-2.5 rounded" style={{ background: '#1E3A5F' }} />
                    Salary {fmt(salary)}/yr
                  </span>
                )}
                {dividends > 0 && (
                  <span className="flex items-center gap-1.5">
                    <span className="inline-block w-2.5 h-2.5 rounded" style={{ background: '#6EE7B7' }} />
                    Dividends {fmt(dividends)}/yr
                  </span>
                )}
                {rentalIncome > 0 && (
                  <span className="flex items-center gap-1.5">
                    <span className="inline-block w-2.5 h-2.5 rounded" style={{ background: '#C9A84C' }} />
                    Rental {fmt(rentalIncome)}/yr
                  </span>
                )}
                {expensesMonthly > 0 && (
                  <span className="flex items-center gap-1.5">
                    <span className="inline-block w-2.5 h-2.5 rounded" style={{ background: '#E5E7EB' }} />
                    Expenses {fmt(expensesMonthly * 12)}/yr
                  </span>
                )}
              </div>
            </div>
          );
        })()}

        {/* Income Sources Card */}
        <div className="bg-white rounded-2xl border border-gray-200 p-6 mb-6">
          <h3 className="text-sm font-bold text-ma-navy mb-4 uppercase tracking-wide">Income Sources</h3>
          <div className="space-y-3">
            {salary > 0 && (
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-700">Salary</span>
                <div className="text-right">
                  <span className="text-sm font-bold text-ma-navy">{fmt(Math.round(salary / 12))}/mo</span>
                  <span className="text-xs text-gray-400 ml-2">{fmt(salary)}/yr</span>
                </div>
              </div>
            )}
            {dividends > 0 && (
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-700">Dividends</span>
                <div className="text-right">
                  <span className="text-sm font-bold text-ma-navy">{fmt(Math.round(dividends / 12))}/mo</span>
                  <span className="text-xs text-gray-400 ml-2">{fmt(dividends)}/yr</span>
                </div>
              </div>
            )}
            {rentalIncome > 0 && (
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-700">Rental income</span>
                <div className="text-right">
                  <span className="text-sm font-bold text-ma-navy">{fmt(Math.round(rentalIncome / 12))}/mo</span>
                  <span className="text-xs text-gray-400 ml-2">{fmt(rentalIncome)}/yr</span>
                </div>
              </div>
            )}
            {expensesMonthly > 0 && (
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-700">Expenses — tax-free</span>
                <div className="text-right">
                  <span className="text-sm font-bold text-ma-navy">{fmt(expensesMonthly)}/mo</span>
                  <span className="text-xs text-gray-400 ml-2">{fmt(expensesMonthly * 12)}/yr</span>
                </div>
              </div>
            )}
            {/* Total */}
            <div className="border-t border-gray-200 pt-3 flex items-center justify-between">
              <span className="text-sm font-bold text-ma-navy">Total gross</span>
              <div className="text-right">
                <span className="text-sm font-bold text-ma-navy">{fmt(Math.round(totalGrossMonthly))}/mo</span>
                <span className="text-xs text-gray-400 ml-2">{fmt(totalGrossAnnual)}/yr</span>
              </div>
            </div>
          </div>
        </div>

        {/* Employee Equivalent Card */}
        <div className="bg-white rounded-2xl border border-gray-200 p-6 mb-6">
          <h3 className="text-sm font-bold text-ma-navy mb-3 uppercase tracking-wide">Employee Equivalent</h3>
          <p className="text-sm text-gray-700 leading-relaxed">
            Your after-tax income is equivalent to an employee earning{' '}
            <span className="font-bold text-ma-navy">{fmt(t.equivalentGrossSalary)}</span> gross.
          </p>
          <p className="text-sm text-gray-600 mt-2">
            That puts you in the <span className="font-bold text-ma-navy">top {topPct}%</span> of UK earners.
          </p>
        </div>

        {/* Earnings Percentile Badge */}
        <div className="bg-white rounded-2xl border border-gray-200 p-6 mb-6">
          <div className="text-center mb-4">
            <div className="inline-block bg-ma-navy text-white rounded-full px-6 py-2.5">
              <span className="text-lg font-bold">Top {topPct}%</span>
              <span className="text-sm ml-1 opacity-80">of UK earners</span>
            </div>
          </div>

          {/* Distribution bar */}
          <div className="mt-4">
            <div className="relative h-6 rounded-full overflow-hidden bg-gradient-to-r from-gray-300 via-blue-300 to-ma-navy">
              {/* Position marker */}
              <div
                className="absolute top-0 h-full w-0.5 bg-white"
                style={{ left: `${percentile}%` }}
              />
              <div
                className="absolute -top-6 transform -translate-x-1/2 text-xs font-bold text-ma-navy whitespace-nowrap"
                style={{ left: `${Math.min(90, Math.max(10, percentile))}%` }}
              >
                {fmt(t.equivalentGrossSalary)}
              </div>
            </div>
            <div className="flex justify-between text-[10px] text-gray-400 mt-1">
              <span>Lowest earners</span>
              <span>Highest earners</span>
            </div>
          </div>
        </div>

        {/* Source */}
        <div className="text-center text-[10px] text-gray-400 mb-6">
          Income percentiles based on HMRC Survey of Personal Incomes 2022/23. Used for context only.
        </div>
      </div>
    </div>
  );
}
